// @flow
import React from 'react';
import {
  compose,
  withProps,
  setDisplayName,
  defaultProps,
  withHandlers,
  onlyUpdateForKeys,
} from 'recompose';
import reactCSS from 'reactcss';
import Transition from 'react-inline-transition-group';
import Panel from '@ux/panel';
import DomainName from './Name';
import DomainPrice from './Price';
import DomainPurchase from './Purchase';
import DomainLegal from './Legal';
import { getStyles } from '@ux/style-utils';
import { AvailabilityStatus, DomainStatus } from '../../../core/constants';
import * as r from 'ramda';
import { isTrue } from '../../../crosscutting/capra';
import stylesheet from './Domain.stylesheet';

export {
  AvailabilityStatus,
  DomainStatus,
};

export const stylesheetToStyles = (stylesheet: Object, {
  isMinSm,
  isMinMd,
  domain,
}) => reactCSS(stylesheet, {
  isMinSm,
  isMinMd,
  inBasket: r.pipe(
    r.prop('availability'),
    r.equals(AvailabilityStatus.AVAILABLE),
  )(domain),
  unavailable: r.pipe(
    r.prop('availability'),
    r.equals(AvailabilityStatus.UNAVAILABLE),
  )(domain),
  unspun: r.propEq('unspun', true, domain),
});

export type PureDomainProps = {
  theme: Object,
  domain: Object,
  isPremium: boolean,
  isAd: boolean,
  isPromo: boolean,
  currency: string,
  styles: Object,
  canTransfer: boolean,
  canBackorder: boolean,
  canNegotiate: boolean,
  onViewBasketClick: Function,
  handlePurchaseDomainClick: Function,
  handleTransferDomainClick: Function,
  handleBackorderDomainClick: Function,
  handleNegotiateDomainClick: Function,
  intlNamespace: string,
  isMinSm: boolean,
  isMinLg: boolean,
  hasLegal: boolean,
  showPrices: boolean,
};

const animationStyles = {
  base: {
    opacity: 0,
    height: 'auto',
    maxHeight: 100,
    overflow: 'hidden',
  },
  appear: {
    opacity: 1,
  },
  enter: {
    opacity: 1,
  },
  leave: {
    opacity: 0,
    maxHeight: 0,
    transition: 'opacity 0.3s ease-in-out, max-height 0.3s ease-in-out',
  },
};

export const PureDomain = ({
  theme,
  domain,
  isPremium,
  isAd,
  isPromo,
  currency,
  styles,
  canTransfer,
  canBackorder,
  canNegotiate,
  handlePurchaseDomainClick,
  handleBackorderDomainClick,
  handleTransferDomainClick,
  handleNegotiateDomainClick,
  onViewBasketClick,
  intlNamespace,
  isMinSm,
  isMinLg,
  hasLegal,
  showPrices,
}: PureDomainProps) => (
  <div>
    <Panel
      className="d-flex"
      component={'div'}
      style={styles.root}
      theme={theme}
    >
      <div style={styles.domainName}>
        <DomainName
          theme={theme}
          domain={domain.fqdn}
          availability={domain.availability}
          isPremium={isPremium}
          isAd={isAd}
          isPromo={isPromo}
          intlNamespace={intlNamespace}
          isMinSm={isMinSm}
        />
      </div>
      <div
        className="d-flex"
        style={styles.domainPurchase}
      >
        {r.when(
          isTrue,
          () => (
            <div style={styles.domainPrices}>
              <DomainPrice
                styles={styles}
                theme={theme}
                price={domain.price}
                oldPrice={domain.oldPrice}
                availability={domain.availability}
                currency={currency}
                intlNamespace={intlNamespace}
                isPromo={isPromo}
                isMinSm={isMinSm}
                hasLegal={hasLegal}
              />
            </div>
          )
        )(showPrices)}
        <div style={styles.domainAction}>
          <DomainPurchase
            theme={theme}
            fqdn={domain.fqdn}
            canTransfer={canTransfer}
            canBackorder={canBackorder}
            canNegotiate={canNegotiate}
            onPurchaseDomainClick={handlePurchaseDomainClick}
            onTransferDomainClick={handleTransferDomainClick}
            onBackorderDomainClick={handleBackorderDomainClick}
            onNegotiateDomainClick={handleNegotiateDomainClick}
            intlNamespace={intlNamespace}
            availability={domain.availability}
            unspun={domain.unspun}
            onViewBasketClick={onViewBasketClick}
            isPromo={isPromo}
            isMinSm={isMinSm}
            isMinLg={isMinLg}
          />
        </div>
      </div>
    </Panel>
    <Transition childrenStyles={animationStyles}>
      {r.ifElse(
        r.propEq('availability', AvailabilityStatus.UNVERIFIED),
        () => (
          <div
            style={styles.legal}
            key="legal"
          >
            <DomainLegal
              theme={theme}
              messages={domain.legalMessages}
            />
          </div>
        ),
        () => <div/>
      )(domain)}
    </Transition>
  </div>
);

const hasLegalMessages = r.pipe(
  r.path([ 'domain', 'legalMessages' ]),
  r.length,
  // $FlowFixMe
  r.gt(r.__, 0),
);

export const enhance = compose(
  setDisplayName('Domain'),
  defaultProps({
    classNamespace: 'Domain',
  }),
  onlyUpdateForKeys([
    'theme',
    'domain',
    'currency',
    'isMinSm',
    'isMinLg',
    'styles',
  ]),
  withProps((ownerProps) => ({
    isPremium: r.pathEq([ 'domain', 'premium' ], true, ownerProps),
    isAd: r.pathEq([ 'domain', 'ad' ], true, ownerProps),
    isPromo: r.pathEq([ 'domain', 'promo' ], true, ownerProps),
    hasLegal: hasLegalMessages(ownerProps),
    styles: getStyles(stylesheet, stylesheetToStyles, ownerProps),
    showPrices: r.pathEq(
      [ 'domain', 'availability' ],
      AvailabilityStatus.UNVERIFIED,
      ownerProps,
    ),
  })),
  withHandlers({
    handlePurchaseDomainClick: ({
      domain,
      onPurchaseDomainClick,
    }) => () => onPurchaseDomainClick(domain.fqdn),
    handleTransferDomainClick: ({
      domain,
      onTransferDomainClick,
    }) => () => onTransferDomainClick(domain.fqdn),
    handleBackorderDomainClick: ({
      domain,
      onBackorderDomainClick,
    }) => () => onBackorderDomainClick(domain.fqdn),
    handleNegotiateDomainClick: ({
      domain,
      onNegotiateDomainClick,
    }) => () => onNegotiateDomainClick(domain.fqdn),
  }),
);

export default enhance(PureDomain);
